import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { Controller, useFieldArray } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { ComboSelect, IconButton, TextboxInput, Typography } from '../../atoms';
import { DatePickerFTDS } from '../../molecules/DatePickerFTDS';
import type { DatePickerFTDSProps } from '../../molecules/DatePickerFTDS';
import { ConfirmationCentered } from '../../molecules/ConfirmationDialog/ConfirmationDialog';
import type { TermOptionItem } from '../../molecules/TermTypeSelect/TermTypeSelect';
import type { RowEditContext } from '../../molecules/EditableTable';
import type { AccountContribution, ClientAccountEntry } from '../../../common/types/clients-entity';
import { checkFFEnabled, exceedsDigitLimit, formatCurrency, isMonthYearDatesEnabled, removeNonNumeric } from '../../../utils';
import {
    CONTRIBUTION_PERIOD_KEYS,
    CONTRIBUTION_FREQUENCY,
    getContributionPeriodLabelKey,
    IncomeSorceDefaultPeriodMapping,
    IncomeSourceDefaultFrequencyMapping
} from '../../../common/contributionPeriodOptionRules';
import type { ContributionPeriodDates } from '../../../common/contributionPeriodOptionRules';
import { ReactComponent as EditIcon } from '../../../assets/icons/pencil_acc.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/trash.svg';
import { ReactComponent as XMarkIcon } from '../../../assets/icons/x-mark.svg';
import { FEATURE_NAMES } from '../../../common/constants';

// All contribution dates (beginningYear/endYear) are stored and persisted in this format,
// matching the rest of the app's date storage convention. UI display formats are separate.
// TODO: replace all occurences of 'DD-MM-YYYY' with a single source of truth from the
//  app's date utility module (or constants).
export const STORAGE_DATE_FORMAT = 'DD-MM-YYYY';
const formatContributionDate = (iso: string, frequency?: string): string => {
    if (!iso) return '';
    const parsed = dayjs(iso, STORAGE_DATE_FORMAT);
    if (!parsed.isValid()) return '';
    // FF off: annual contributions display as a bare year.
    if (!isMonthYearDatesEnabled() && frequency === 'annually') {
        return parsed.format('YYYY');
    }
    return parsed.format('MMM YYYY');
};

export type OptionItem = { label: string; value: string; firstName?: string | undefined };

/**
 * DatePickerFTDS bound to react-hook-form. The picker's own min/max error is folded into this
 * field's `validate` rule (via a ref) because RHF clears a manually `setError`'d error as soon as
 * the field revalidates on change — so the rule itself has to be the single source of truth.
 */
const ValidatedDatePickerFTDS = ({
    form,
    name,
    requiredMsg,
    startFieldName,
    onValueChange,
    ...pickerProps
}: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: any;
    name: string;
    requiredMsg?: boolean;
    /** When set, this field must resolve to a date strictly after the live value of `startFieldName`. */
    startFieldName?: string;
    minDateErrorMsg?: string;
    maxDateErrorMsg?: string;
    // eslint-disable-next-line no-unused-vars
    onValueChange?: (value: string) => void;
} & Omit<DatePickerFTDSProps, 'value' | 'onChange' | 'error' | 'helperText'>) => {
    const { t } = useTranslation();
    // Tracks whether the picker's own onError put a manual error on this field, so we only
    // recompute (trigger) when clearing our error — never wiping a legit validate-rule error.
    const pickerErrorRef = useRef(false);
    // Prefilled dates can trip the picker's min/max on load; only honour picker errors once the
    // user has actually focused/edited this field (focus also covers a typed, invalid 0000).
    const hasInteractedRef = useRef(false);
    return (
        <Controller
            control={form.control}
            name={name}
            rules={{
                validate: (v: string) => {
                    if (requiredMsg && !v) return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    if (!v) return true;

                    const dateValue = dayjs(v as unknown as Dayjs | string, STORAGE_DATE_FORMAT);
                    // Invalid dates (e.g. a 0000 year) fail every comparison, so reject them explicitly.
                    if (!dateValue.isValid()) return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    if (dateValue.isBefore(dayjs(), 'day')) return t('GOALS_INLINE_DATE_ERROR');
                    // Read the sibling start value live so the comparison stays correct even when the
                    // start field changed after this field was last rendered.
                    if (startFieldName) {
                        const startValue = form.getValues(startFieldName);
                        if (startValue) {
                            const compareDate = dayjs(startValue, STORAGE_DATE_FORMAT);
                            if (!dateValue.isAfter(compareDate, 'day')) {
                                return t('END_DATE_AFTER_START_ERROR');
                            }
                        }
                    }

                    return true;
                }
            }}
            render={({ field: f, fieldState }) => {
                const fieldErrorMsg = fieldState.error?.message as string | undefined;
                return (
                    <Box
                        sx={{ display: 'contents' }}
                        onFocus={() => {
                            hasInteractedRef.current = true;
                        }}
                    >
                        <DatePickerFTDS
                            {...pickerProps}
                            value={f.value ? dayjs(f.value, STORAGE_DATE_FORMAT) : null}
                            onChange={(d) => {
                                const iso = d ? d.startOf('month').format(STORAGE_DATE_FORMAT) : '';
                                f.onChange(iso);
                                onValueChange?.(iso);
                            }}
                            error={!!fieldErrorMsg}
                            minDateErrorMsg={fieldErrorMsg}
                            onValidationError={(pickerError?: string | null) => {
                                // Skip prefilled/initial picker errors; only enforce after the user
                                // has interacted with the field (typing an invalid 0000 included).
                                if (!hasInteractedRef.current) return;
                                // An invalid entry (e.g. a 0000 year) never reaches onChange, so the
                                // validate rule never runs; register/recompute the error here to gate Save.
                                if (pickerError) {
                                    pickerErrorRef.current = true;
                                    form.setError(name, {
                                        type: 'manual',
                                        message: t('TEXT_THIS_IS_REQUIRED_FIELD')
                                    });
                                } else if (pickerErrorRef.current) {
                                    pickerErrorRef.current = false;
                                    form.trigger(name);
                                }
                            }}
                        />
                    </Box>
                );
            }}
        />
    );
};

const CONTRIBUTION_COLUMN_WIDTHS = {
    type: '20%',
    accountHolder: '18%',
    amount: '18%',
    frequency: '14%',
    period: '20%',
    actions: '10%'
};

export interface ContributionsEditorProps {
    form: RowEditContext<ClientAccountEntry>;
    onPromoteAccount?: () => void;
    typeOptions: TermOptionItem[];
    accountHolderOptions: OptionItem[];
    accountHolderLabelByValue: Record<string, string>;
    frequencyOptions: OptionItem[];
    contributionPeriodOptions: OptionItem[];
    // eslint-disable-next-line no-unused-vars
    getContributionPeriodDates: (
        // eslint-disable-next-line no-unused-vars
        periodKey: string,
        // eslint-disable-next-line no-unused-vars
        accountHolderCode: string,
        // eslint-disable-next-line no-unused-vars
        frequency?: string
    ) => ContributionPeriodDates;
    // eslint-disable-next-line no-unused-vars
    deriveContributionPeriodKey: (start: string, end: string, accountHolderCode: string, frequency?: string) => string;
    // eslint-disable-next-line no-unused-vars
    formatContributionPeriodRange: (start: string, end: string) => string;
    currencySymbol: string;
    readOnly?: boolean;
}

export const ContributionsEditor = ({
    form,
    onPromoteAccount,
    typeOptions,
    accountHolderOptions,
    accountHolderLabelByValue,
    frequencyOptions,
    contributionPeriodOptions,
    getContributionPeriodDates,
    deriveContributionPeriodKey,
    formatContributionPeriodRange,
    currencySymbol,
    readOnly
}: ContributionsEditorProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const typeLabelByValue = useMemo(
        () => typeOptions.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {} as Record<string, string>),
        [typeOptions]
    );
    const { fields, append, remove, update } = useFieldArray<ClientAccountEntry, 'contributions', 'fieldId'>({
        control: form.control,
        name: 'contributions',
        keyName: 'fieldId'
    });
    const liveContributions = (form.watch('contributions') ?? []) as AccountContribution[];
    const contributionsCount = liveContributions.length;

    useEffect(() => {
        if (contributionsCount > 0) {
            form.clearErrors('amount');
        } else {
            form.trigger('amount');
        }
    }, [contributionsCount]);

    const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
    const [activeEditingId, setActiveEditingId] = useState<string | null>(null);
    const [contributionPeriodByRow, setContributionPeriodByRow] = useState<Record<string, string>>({});
    const [pendingDelete, setPendingDelete] = useState<{ index: number; row: AccountContribution } | null>(null);
    const snapshotsRef = useRef<Map<string, AccountContribution>>(new Map());
    const newRowIdsRef = useRef<Set<string>>(new Set());

    const addEditing = (id: string) =>
        setEditingIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });

    const removeEditing = (id: string) =>
        setEditingIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

    const startEdit = (row: AccountContribution) => {
        snapshotsRef.current.set(row.localId, { ...row });
        setContributionPeriodByRow((prev) => ({
            ...prev,
            [row.localId]: deriveContributionPeriodKey(row.beginningYear, row.endYear, row.sourceOwner, row.frequency)
        }));
        addEditing(row.localId);
        setActiveEditingId(row.localId);
        onPromoteAccount?.();
    };

    const cancelEdit = (index: number, row: AccountContribution) => {
        form.clearErrors([
            `contributions.${index}.sourceType`,
            `contributions.${index}.sourceOwner`,
            `contributions.${index}.amount`,
            `contributions.${index}.frequency`,
            `contributions.${index}.beginningYear`,
            `contributions.${index}.endYear`
        ]);
        if (newRowIdsRef.current.has(row.localId)) {
            newRowIdsRef.current.delete(row.localId);
            remove(index);
        } else {
            const snapshot = snapshotsRef.current.get(row.localId);
            if (snapshot) update(index, snapshot);
        }
        removeEditing(row.localId);
        setActiveEditingId((prev) => (prev === row.localId ? null : prev));
    };

    const handleAdd = () => {
        const newId = uuidv4();
        const defaultAccountHolder = accountHolderOptions[0]?.value ?? '';
        const defaultSourceType = typeOptions[0] ?? { value: '', duration: '' };
        const defaultFrequency =
            IncomeSourceDefaultFrequencyMapping[defaultSourceType.value] ?? frequencyOptions[0]?.value ?? '';

        // Resolve the default period for this source type from the label→period mapping;
        // fall back to NOW_TO_RETIREMENT when the type has no explicit mapping.
        const defaultPeriodKey =
            IncomeSorceDefaultPeriodMapping[defaultSourceType.value] ?? CONTRIBUTION_PERIOD_KEYS.NOW_TO_RETIREMENT;

        let start = '';
        let end = '';
        if (defaultPeriodKey === CONTRIBUTION_PERIOD_KEYS.CUSTOM) {
            const unit: 'year' | 'month' = isMonthYearDatesEnabled()
                ? 'month'
                : defaultFrequency === 'annually'
                ? 'year'
                : 'month';
            start = dayjs().add(1, unit).startOf(unit).format(STORAGE_DATE_FORMAT);
        } else {
            ({ start, end } = getContributionPeriodDates(defaultPeriodKey, defaultAccountHolder, defaultFrequency));
        }

        newRowIdsRef.current.add(newId);
        setContributionPeriodByRow((prev) => ({ ...prev, [newId]: CONTRIBUTION_PERIOD_KEYS.NOW_TO_RETIREMENT }));
        append({
            localId: newId,
            sourceType: defaultSourceType.value,
            term: defaultSourceType.duration,
            sourceOwner: defaultAccountHolder,
            amount: 0,
            frequency: defaultFrequency,
            beginningYear: start,
            endYear: end
        });
        addEditing(newId);
        setActiveEditingId(newId);
        onPromoteAccount?.();
    };

    // Auto-open a blank contribution in edit view when the area expands with no rows,
    // unless the editor is in read-only mode.
    const didAutoOpenRef = useRef(false);
    useEffect(() => {
        if (didAutoOpenRef.current) return;
        if (readOnly) return;
        if (fields.length === 0) {
            didAutoOpenRef.current = true;
            handleAdd();
        }
    }, [readOnly, fields.length]);

    const handleDelete = (index: number, row: AccountContribution) => {
        didAutoOpenRef.current = true;
        onPromoteAccount?.();
        newRowIdsRef.current.delete(row.localId);
        snapshotsRef.current.delete(row.localId);
        removeEditing(row.localId);
        setActiveEditingId((prev) => (prev === row.localId ? null : prev));
        remove(index);
    };

    const handleContributionPeriodChange = (
        index: number,
        rowId: string,
        accountHolderCode: string,
        periodKey: string,
        frequency?: string
    ) => {
        setContributionPeriodByRow((prev) => ({ ...prev, [rowId]: periodKey }));
        const isCustom = periodKey === CONTRIBUTION_PERIOD_KEYS.CUSTOM;
        let start = '';
        let end = '';
        if (!isCustom) {
            ({ start, end } = getContributionPeriodDates(periodKey, accountHolderCode, frequency));
        }

        form.setValue(`contributions.${index}.beginningYear`, start, { shouldDirty: true, shouldValidate: true });
        form.setValue(`contributions.${index}.endYear`, end, { shouldDirty: true, shouldValidate: true });

        if (isCustom) {
            // Blank custom pickers must keep the row invalid (Save disabled) until the user
            // picks both a start and an end date.
            form.trigger([`contributions.${index}.beginningYear`, `contributions.${index}.endYear`]);
        } else {
            // Non-custom options always resolve to known dates, so clear any stale required
            // errors left behind by the custom date pickers to re-enable Save.
            form.clearErrors([`contributions.${index}.beginningYear`, `contributions.${index}.endYear`]);
        }
    };

    const handleAccountHolderChange = (index: number, rowId: string, accountHolderCode: string) => {
        form.setValue(`contributions.${index}.sourceOwner`, accountHolderCode, { shouldDirty: true });
        const periodKey = contributionPeriodByRow[rowId];
        if (periodKey && periodKey !== CONTRIBUTION_PERIOD_KEYS.CUSTOM) {
            const { start, end } = getContributionPeriodDates(
                periodKey,
                accountHolderCode,
                form.getValues(`contributions.${index}.frequency`)
            );
            form.setValue(`contributions.${index}.beginningYear`, start, { shouldDirty: true, shouldValidate: true });
            form.setValue(`contributions.${index}.endYear`, end, { shouldDirty: true, shouldValidate: true });
        }
    };

    const handleFrequencyChange = (index: number, rowId: string, newFrequency: string) => {
        const prevFrequency = form.getValues(`contributions.${index}.frequency`);
        form.setValue(`contributions.${index}.frequency`, newFrequency, { shouldDirty: true });

        if (newFrequency === CONTRIBUTION_FREQUENCY.ONE_TIME) {
            // One Time: end date mirrors the single chosen (beginning) date.
            const begin = form.getValues(`contributions.${index}.beginningYear`) ?? '';
            form.setValue(`contributions.${index}.endYear`, begin, { shouldDirty: true, shouldValidate: true });
            form.trigger([`contributions.${index}.beginningYear`, `contributions.${index}.endYear`]);
        } else if (prevFrequency === CONTRIBUTION_FREQUENCY.ONE_TIME) {
            // Leaving One Time: fall back to a blank Custom range so the user re-picks the dates.
            setContributionPeriodByRow((prev) => ({ ...prev, [rowId]: CONTRIBUTION_PERIOD_KEYS.CUSTOM }));
            form.setValue(`contributions.${index}.endYear`, '', { shouldDirty: true, shouldValidate: true });
            form.trigger([`contributions.${index}.beginningYear`, `contributions.${index}.endYear`]);
        }
    };

    return (
        <Box>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', alignItems: 'flex-end' }}>
            <Box>
                <Typography label={t('CONTRIBUTION_FUNDING')} variant="headers.h5" sx={{ marginBottom: '4px', fontSize: 14, lineHeight: '19px', fontWeight: 700 }} />
                <Typography
                    label={t('CONTRIBUTION_FUNDING_HELPER')}
                    variant="body.small"
                    sx={{ display: 'block', color: theme.palette.semantic.text.default, fontSize: 14, fontWeight: 400 }}
                />
            </Box>
            <Box display="flex" justifyContent="flex-end" sx={{ marginTop: '12px' }}>
                <Button
                    variant="outlined"
                    disabled={readOnly}
                    onClick={handleAdd}
                    sx={{ borderRadius: '24px', textTransform: 'none' }}
                >
                    {t('ADD_CONTRIBUTIONS')}
                </Button>
            </Box>
            </div>
            <Box
                sx={{
                    border: `1px solid ${theme.palette.semantic.bd.default}`,
                    borderRadius: '8px',
                    backgroundColor: theme.palette.background.paper,
                    overflow: 'hidden',
                    '& > .MuiTable-root > .MuiTableHead-root > .MuiTableRow-root > .MuiTableCell-root': {
                        backgroundColor: theme.palette.semantic.bg.raised,
                        height: '48px',
                        verticalAlign: 'middle',
                        fontSize: '14px',
                        fontWeight: 700,
                        borderBottom: `1px solid ${theme.palette.semantic.bd.default}`
                    }
                }}
            >
                <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: CONTRIBUTION_COLUMN_WIDTHS.type }}>{t('TYPE')}</TableCell>
                            <TableCell sx={{ width: CONTRIBUTION_COLUMN_WIDTHS.accountHolder }}>
                                {t('HOLDER')}
                            </TableCell>
                            <TableCell align="right" sx={{ width: CONTRIBUTION_COLUMN_WIDTHS.amount }}>
                                {t('AMOUNT_LABEL')}
                            </TableCell>
                            <TableCell sx={{ width: CONTRIBUTION_COLUMN_WIDTHS.frequency }}>
                                {t('FREQUENCY')}
                            </TableCell>
                            <TableCell sx={{ width: CONTRIBUTION_COLUMN_WIDTHS.period }}>
                                {t('CONTRIBUTION_PERIOD')}
                            </TableCell>
                            <TableCell align="left" sx={{ width: CONTRIBUTION_COLUMN_WIDTHS.actions }}>
                                {t('ACTIONS')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fields.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ padding: '16px 0' }}>
                                    <Typography
                                        label={t('NO_CONTRIBUTIONS_YET')}
                                        variant="body.small"
                                        sx={{ color: theme.palette.semantic.text.muted }}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        {fields.map((field, index) => {
                            const live = liveContributions[index] ?? (field as unknown as AccountContribution);
                            const rowId = (field as unknown as AccountContribution).localId;
                            const isEditing = editingIds.has(rowId);
                            const isActive = isEditing && activeEditingId === rowId;
                            const isLast = index === fields.length - 1;

                            if (isEditing) {
                                return (
                                    <TableRow
                                        key={field.fieldId}
                                        onFocusCapture={() => setActiveEditingId(rowId)}
                                        onMouseDownCapture={() => setActiveEditingId(rowId)}
                                        onTouchStartCapture={() => setActiveEditingId(rowId)}
                                        sx={(muiTheme) => ({
                                            '& > .MuiTableCell-root': {
                                                verticalAlign: 'top',
                                                borderTop: isActive
                                                    ? `1px solid ${muiTheme.palette.semantic.int.selected}`
                                                    : 'none',
                                                borderBottom: isActive
                                                    ? `1px solid ${muiTheme.palette.semantic.int.selected}`
                                                    : 'none'
                                            },
                                            '& > .MuiTableCell-root:first-of-type': {
                                                ...(isActive && {
                                                    borderLeft: `1px solid ${muiTheme.palette.semantic.int.selected}`,
                                                    ...(isLast && { borderBottomLeftRadius: '8px' })
                                                })
                                            },
                                            '& > .MuiTableCell-root:last-of-type': {
                                                ...(isActive && {
                                                    borderRight: `1px solid ${muiTheme.palette.semantic.int.selected}`,
                                                    ...(isLast && { borderBottomRightRadius: '8px' })
                                                })
                                            }
                                        })}
                                    >
                                        <TableCell sx={{ padding: '8px' }}>
                                            <Controller
                                                control={form.control}
                                                name={`contributions.${index}.sourceType`}
                                                rules={{ required: t('VALIDATION_TYPE_REQUIRED') }}
                                                render={({ field: f, fieldState }) => (
                                                    <ComboSelect
                                                        options={typeOptions}
                                                        selectedValue={f.value ?? ''}
                                                        onSelectChange={(v) => {
                                                            const val = Array.isArray(v) ? v[0] ?? '' : v;
                                                            f.onChange(val);
                                                            const term =
                                                                typeOptions.find((opt) => opt.value === val)
                                                                    ?.duration ?? '';
                                                            form.setValue(`contributions.${index}.term`, term, {
                                                                shouldDirty: true
                                                            });
                                                            // Apply the mapped default frequency first. Inheritance
                                                            // defaults to "One Time" (single date picker); every other
                                                            // type resets to the standard frequency so the period rule
                                                            // below takes effect (this also switches back out of the
                                                            // One Time control when moving off Inheritance).
                                                            const defaultFrequency =
                                                                IncomeSourceDefaultFrequencyMapping[val] ??
                                                                frequencyOptions[0]?.value ??
                                                                '';
                                                            handleFrequencyChange(index, rowId, defaultFrequency);
                                                            // Apply the mapped default period (start/end dates) for the
                                                            // newly-selected type.
                                                            const newPeriodKey =
                                                                IncomeSorceDefaultPeriodMapping[val] ??
                                                                CONTRIBUTION_PERIOD_KEYS.NOW_TO_RETIREMENT;
                                                            handleContributionPeriodChange(
                                                                index,
                                                                rowId,
                                                                live.sourceOwner,
                                                                newPeriodKey,
                                                                form.getValues(`contributions.${index}.frequency`)
                                                            );
                                                        }}
                                                        size="small"
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px' }}>
                                            <Controller
                                                control={form.control}
                                                name={`contributions.${index}.sourceOwner`}
                                                rules={{ required: t('VALIDATION_HOLDER_REQUIRED') }}
                                                render={({ field: f, fieldState }) => (
                                                    <ComboSelect
                                                        options={accountHolderOptions}
                                                        selectedValue={f.value ?? ''}
                                                        onSelectChange={(v) =>
                                                            handleAccountHolderChange(
                                                                index,
                                                                rowId,
                                                                Array.isArray(v) ? v[0] ?? '' : v
                                                            )
                                                        }
                                                        size="small"
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px' }} align="right">
                                            <Controller
                                                control={form.control}
                                                name={`contributions.${index}.amount`}
                                                rules={{
                                                    required: t('VALIDATION_AMOUNT_REQUIRED'),
                                                    validate: {
                                                        positive: (v) =>
                                                            Number(v) > 0 || t('VALIDATION_AMOUNT_REQUIRED'),
                                                        maxDigits: (v) =>
                                                            !exceedsDigitLimit(v, 15) ||
                                                            t('CONTRIBUTION_AMOUNT_MAXLENGTH_ERROR')
                                                    }
                                                }}
                                                render={({ field: f, fieldState }) => (
                                                    <TextboxInput
                                                        type="currency"
                                                        size="small"
                                                        placeholder={currencySymbol}
                                                        value={f.value ? formatCurrency(+f.value, true) : ''}
                                                        onChange={(e) => {
                                                            const numericValue = removeNonNumeric(e.target.value);
                                                            f.onChange(numericValue ? Number(numericValue) : 0);
                                                        }}
                                                        error={!!fieldState.error}
                                                        showTooltipError={true}
                                                        openTooltipError={
                                                            !!fieldState.error &&
                                                            form.errors?.contributions?.length === 1
                                                        }
                                                        helperText={fieldState.error?.message as string | undefined}
                                                        fullWidth
                                                        sx={{ '& input': { textAlign: 'right' } }}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px' }}>
                                            <Controller
                                                control={form.control}
                                                name={`contributions.${index}.frequency`}
                                                rules={{ required: t('VALIDATION_FREQUENCY_REQUIRED') }}
                                                render={({ field: f, fieldState }) => (
                                                    <ComboSelect
                                                        options={frequencyOptions}
                                                        selectedValue={f.value ?? ''}
                                                        onSelectChange={(v) => {
                                                            const newFrequency = Array.isArray(v) ? v[0] ?? '' : v;
                                                            f.onChange(newFrequency);
                                                            const periodKey = contributionPeriodByRow[rowId];
                                                            if (isMonthYearDatesEnabled()) {
                                                                if (
                                                                    newFrequency ===
                                                                    CONTRIBUTION_FREQUENCY.ONE_TIME
                                                                ) {
                                                                    form.setValue(
                                                                        `contributions.${index}.beginningYear`,
                                                                        '',
                                                                        { shouldDirty: true, shouldValidate: false }
                                                                    );
                                                                    form.setValue(
                                                                        `contributions.${index}.endYear`,
                                                                        '',
                                                                        { shouldDirty: true, shouldValidate: false }
                                                                    );
                                                                } else if (
                                                                    periodKey !== CONTRIBUTION_PERIOD_KEYS.CUSTOM
                                                                ) {
                                                                    const { start, end } =
                                                                        getContributionPeriodDates(
                                                                            periodKey,
                                                                            live.sourceOwner,
                                                                            newFrequency
                                                                        );
                                                                    form.setValue(
                                                                        `contributions.${index}.beginningYear`,
                                                                        start,
                                                                        { shouldDirty: true, shouldValidate: false }
                                                                    );
                                                                    form.setValue(
                                                                        `contributions.${index}.endYear`,
                                                                        end,
                                                                        { shouldDirty: true, shouldValidate: false }
                                                                    );
                                                                }
                                                            } else if (
                                                                periodKey === CONTRIBUTION_PERIOD_KEYS.CUSTOM
                                                            ) {
                                                                // FF off: custom clears its range on frequency change.
                                                                form.setValue(
                                                                    `contributions.${index}.beginningYear`,
                                                                    '',
                                                                    { shouldDirty: true, shouldValidate: false }
                                                                );
                                                                form.setValue(
                                                                    `contributions.${index}.endYear`,
                                                                    '',
                                                                    { shouldDirty: true, shouldValidate: false }
                                                                );
                                                            } else {
                                                                // FF off: presets recalc to the new frequency's dates.
                                                                const { start, end } = getContributionPeriodDates(
                                                                    periodKey,
                                                                    live.sourceOwner,
                                                                    newFrequency
                                                                );
                                                                form.setValue(
                                                                    `contributions.${index}.beginningYear`,
                                                                    start,
                                                                    { shouldDirty: true, shouldValidate: false }
                                                                );
                                                                form.setValue(
                                                                    `contributions.${index}.endYear`,
                                                                    end,
                                                                    { shouldDirty: true, shouldValidate: false }
                                                                );
                                                            }
                                                        }}
                                                        size="small"
                                                        fullWidth
                                                        error={!!fieldState.error}
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ padding: '8px' }}>
                                            {(() => {
                                                // "One Time" contributions use a single MM/YYYY date picker;
                                                // beginningYear holds the chosen date and endYear mirrors it.
                                                const isOneTime =
                                                    form.watch(`contributions.${index}.frequency`) ===
                                                    CONTRIBUTION_FREQUENCY.ONE_TIME;
                                                if (isOneTime) {
                                                    return (
                                                        <ValidatedDatePickerFTDS
                                                            form={form}
                                                            name={`contributions.${index}.beginningYear`}
                                                            selectionType="month"
                                                            startDate={dayjs().add(1, 'month').startOf('month')}
                                                            requiredMsg={true}
                                                            onValueChange={(iso) =>
                                                                // End date always mirrors the single date.
                                                                form.setValue(`contributions.${index}.endYear`, iso, {
                                                                    shouldDirty: true,
                                                                    shouldValidate: true
                                                                })
                                                            }
                                                        />
                                                    );
                                                }
                                                const periodKey =
                                                    contributionPeriodByRow[rowId] ??
                                                    deriveContributionPeriodKey(
                                                        live.beginningYear,
                                                        live.endYear,
                                                        live.sourceOwner,
                                                        live.frequency
                                                    );
                                                const isCustom = periodKey === CONTRIBUTION_PERIOD_KEYS.CUSTOM;
                                                // FF off: annual custom ranges pick whole years.
                                                const customUnit: 'year' | 'month' = isMonthYearDatesEnabled()
                                                    ? 'month'
                                                    : live.frequency === 'annually'
                                                    ? 'year'
                                                    : 'month';
                                                return (
                                                    <Box display="flex" flexDirection="column" gap="6px">
                                                        <ComboSelect
                                                            options={contributionPeriodOptions}
                                                            selectedValue={periodKey}
                                                            onSelectChange={(v) =>
                                                                handleContributionPeriodChange(
                                                                    index,
                                                                    rowId,
                                                                    live.sourceOwner,
                                                                    Array.isArray(v) ? v[0] ?? '' : v,
                                                                    form.getValues(`contributions.${index}.frequency`)
                                                                )
                                                            }
                                                            size="small"
                                                            fullWidth
                                                        />
                                                        {isCustom ? (
                                                            <Box display="flex" gap="8px">
                                                                <ValidatedDatePickerFTDS
                                                                    form={form}
                                                                    name={`contributions.${index}.beginningYear`}
                                                                    selectionType={customUnit}
                                                                    startDate={dayjs()
                                                                        .add(1, customUnit)
                                                                        .startOf(customUnit)}
                                                                    requiredMsg={true}
                                                                    onValueChange={() =>
                                                                        // Re-validate the end date: it must stay after
                                                                        // the (now changed) beginning date.
                                                                        form.trigger(
                                                                            `contributions.${index}.endYear`
                                                                        )
                                                                    }
                                                                />
                                                                <ValidatedDatePickerFTDS
                                                                    form={form}
                                                                    name={`contributions.${index}.endYear`}
                                                                    startFieldName={`contributions.${index}.beginningYear`}
                                                                    disabled={
                                                                        !form.watch(
                                                                            `contributions.${index}.beginningYear`
                                                                        )
                                                                    }
                                                                    selectionType={customUnit}
                                                                    requiredMsg={true}
                                                                    startDate={(() => {
                                                                        const beginningYear = form.watch(
                                                                            `contributions.${index}.beginningYear`
                                                                        );
                                                                        if (!beginningYear) return undefined;
                                                                        return dayjs(
                                                                            beginningYear,
                                                                            STORAGE_DATE_FORMAT
                                                                        ).startOf(customUnit);
                                                                    })()}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Typography
                                                                label={formatContributionPeriodRange(
                                                                    live.beginningYear,
                                                                    live.endYear
                                                                )}
                                                                variant="body.small"
                                                                sx={{ color: theme.palette.semantic.text.muted }}
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell align="center" sx={{ padding: '8px' }}>
                                            <IconButton
                                                iconName={<XMarkIcon />}
                                                arialabel={t('BUTTON_CANCEL')}
                                                onClick={() => cancelEdit(index, live)}
                                                sx={(muiTheme) => ({
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    backgroundColor: muiTheme.palette.background.paper,
                                                    border: `1px solid ${muiTheme.palette.semantic.icon.brand}`,
                                                    '&:hover': { backgroundColor: muiTheme.palette.semantic.int.hover }
                                                })}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            return (
                                <TableRow
                                    key={field.fieldId}
                                    sx={{ '& > .MuiTableCell-root': { verticalAlign: 'top' } }}
                                >
                                    <TableCell>
                                        <Typography
                                            label={typeLabelByValue[live.sourceType] || live.sourceType || '-'}
                                            variant="body.large"
                                            sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            label={
                                                accountHolderLabelByValue[live.sourceOwner] || live.sourceOwner || '-'
                                            }
                                            variant="body.large"
                                            sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography
                                            label={formatCurrency(Number(live.amount) || 0)}
                                            variant="body.large"
                                            sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            label={
                                                frequencyOptions.find((opt) => opt.value === live.frequency)?.label ||
                                                live.frequency ||
                                                '-'
                                            }
                                            variant="body.large"
                                            sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" flexDirection="column" gap={0.25}>
                                            {live.frequency === CONTRIBUTION_FREQUENCY.ONE_TIME ? (
                                                <Typography
                                                    label={
                                                        live.beginningYear
                                                            ? dayjs(live.beginningYear, STORAGE_DATE_FORMAT).format(
                                                                  'MMM YYYY'
                                                              )
                                                            : '-'
                                                    }
                                                    variant="body.large"
                                                    sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                                                />
                                            ) : (
                                                <>
                                                    <Typography
                                                        label={t(
                                                            getContributionPeriodLabelKey(
                                                                deriveContributionPeriodKey(
                                                                    live.beginningYear,
                                                                    live.endYear,
                                                                    live.sourceOwner,
                                                                    live.frequency
                                                                )
                                                            )
                                                        )}
                                                        variant="body.large"
                                                        sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                                                    />
                                                    {live.beginningYear && live.endYear && (
                                                        <Typography
                                                            label={`${formatContributionDate(
                                                                live.beginningYear,
                                                                live.frequency
                                                            )} - ${formatContributionDate(
                                                                live.endYear,
                                                                live.frequency
                                                            )}`}
                                                            variant="body.small"
                                                            sx={{ color: theme.palette.semantic.text.muted, fontWeight: 400 }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" gap={1} justifyContent="center">
                                            <IconButton
                                                iconName={<EditIcon />}
                                                arialabel={t('BUTTON_EDIT')}
                                                onClick={() => startEdit(live)}
                                                disabled={readOnly}
                                                sx={{
                                                    opacity: readOnly ? 0.5 : 1,
                                                    pointerEvents: readOnly ? 'none' : 'auto'
                                                }}
                                            />
                                            <IconButton
                                                iconName={<DeleteIcon />}
                                                arialabel={t('DELETE_TEXT')}
                                                onClick={() => setPendingDelete({ index, row: live })}
                                                disabled={readOnly}
                                                sx={{
                                                    opacity: readOnly ? 0.5 : 1,
                                                    pointerEvents: readOnly ? 'none' : 'auto'
                                                }}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            {pendingDelete && (
                <ConfirmationCentered
                    open
                    title={t('DELETE_FUNDING_TITLE')}
                    content={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_FUNDING_CONTENT_FTDS'
                            : 'DELETE_FUNDING_CONTENT'
                    )}
                    confirmButtonLabel={t('DELETE_TEXT')}
                    cancelButtonLabel={t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}
                    onConfirmation={() => {
                        handleDelete(pendingDelete.index, pendingDelete.row);
                        setPendingDelete(null);
                    }}
                    onCancellation={() => setPendingDelete(null)}
                    onCloseIconClick={() => setPendingDelete(null)}
                />
            )}
        </Box>
    );
};
